import {
  Box,
  Button,
  ButtonGroup,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react"
import { Checkbox } from "@/components/ui/checkbox";
import { Select } from "@/components/ui/select";
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { useForm, type SubmitHandler } from "react-hook-form"
import { FaPlus } from "react-icons/fa"
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog"
import { Field } from "../ui/field"
import { ReportsService, type ReportJobCreate, type ReportJobType, type ReportJobSchedule } from "@/client"
import type { ApiError } from "../../client/core/ApiError"
import useCustomToast from "../../hooks/useCustomToast"
import { handleError } from "../../utils"
import {
  reportTypeOptions,
  scheduleOptions,
} from "@/types/report"

const ReportSetting = () => {
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()
  const [isOpen, setIsOpen] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [selectedFeedIds, setSelectedFeedIds] = useState<string[]>([])
  const [selectedType, setSelectedType] = useState<ReportJobType>("briefing")
  const [selectedSchedule, setSelectedSchedule] = useState<ReportJobSchedule>("once")
  const [startTime, setStartTime] = useState("08:00")
  const [endDate, setEndDate] = useState(() => {
    const today = new Date()
    return today.toISOString().split("T")[0] // "YYYY-MM-DD"
  })

  const toggleFeedSelection = (feedId: string) => {
    setSelectedFeedIds((prev) =>
      prev.includes(feedId) 
        ? prev.filter((id) => id !== feedId) 
        : [...prev, feedId]
    )
  }
  

  const { data } = useQuery({
    queryKey: ["feedMetadata"],
    queryFn: () => ReportsService.readFeedMetadata(),
  })

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { isValid, isSubmitting },
  } = useForm<ReportJobCreate>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      feed_ids: [],
      report_job_name: "",
      description: "",
      report_type: "briefing",
      schedule: "once", 
    },
  })
  useEffect(() => {
    setValue("feed_ids", selectedFeedIds, { shouldValidate: true })
  }, [selectedFeedIds])

  const mutation = useMutation({
    mutationFn: (data: ReportJobCreate) => ReportsService.createReportJob({ requestBody: data }),
    onSuccess: () => {
      showSuccessToast("리포트 생성 작업이 성공적으로 등록되었습니다.")
      queryClient.invalidateQueries({ queryKey: ["reportJobs"] })
      reset()
      setSelectedFeedIds([])
      setSelectedType("briefing")
      setSelectedSchedule("once")
      setHasSubmitted(false)
      setIsOpen(false)
    },
    onError: (err: ApiError) => {
      handleError(err)
      setHasSubmitted(false)
    },
  })

  const onSubmit: SubmitHandler<ReportJobCreate> = (formData) => {
    if (hasSubmitted) return
    setHasSubmitted(true)

    // ✅ feed_ids, report_type, schedule을 수동으로 form에 반영
    setValue("feed_ids", selectedFeedIds)
    setValue("report_type", selectedType)
    setValue("schedule", selectedSchedule) // optional field
    mutation.mutate({
      ...formData,
      feed_ids: selectedFeedIds,
      report_type: selectedType as ReportJobType,
      schedule: selectedSchedule as ReportJobSchedule,
      ...(selectedSchedule !== "once" && {
        start_time: startTime,
        end_date: endDate,
      }),
    })
  }

  const filteredScheduleOptions = scheduleOptions.filter(
    (option) => option.value !== "stop"
  )

  return (
    <DialogRoot
      size={{ base: "xs", md: "md" }}
      placement="center"
      open={isOpen}
      onOpenChange={({ open }) => {
        setIsOpen(open)
        if (open) {
          setSelectedFeedIds([]) // Dialog 열릴 때 초기화하고 싶다면
        }
      }}
    >
      <DialogTrigger asChild>
        <Button my={4}>
          <FaPlus fontSize="16px" />
          작업 추가
        </Button>
      </DialogTrigger>

      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>리포트 생성</DialogTitle>
          </DialogHeader>

          <DialogBody>
            <Text mb={4}>새 리포트를 생성하려면 세부 정보를 입력하세요.</Text>
            <VStack gap={4} align="start">
              <Field label="리포트 이름" required>
                <Input {...register("report_job_name", { required: "리포트 이름은 필수입니다." })} />
              </Field>

              <Field label="리포트 설명">
                <Input {...register("description")} />
              </Field>

              <Field label="피드 선택" required />
              <Box maxH="200px" overflowY="auto" w="100%" pr={2}>
                <VStack gap={4} align="start">
                  {data?.data?.map((feed) => (
                    <Checkbox
                      key={feed.id}
                      checked={selectedFeedIds.includes(feed.id)}
                      onCheckedChange={() => toggleFeedSelection(feed.id)}
                      mb={2}
                    >
                      <Box>
                        <Text fontWeight="medium">{feed.feed_name}</Text>
                        <Text fontSize="xs" color="gray.500">{feed.feed_description}</Text>
                      </Box>
                    </Checkbox>
                  ))}
                </VStack>
              </Box>
              <Input
                hidden
                {...register("feed_ids", {
                  validate: () =>
                    selectedFeedIds.length > 0 || "피드를 하나 이상 선택하세요.",
                })}
                value={selectedFeedIds.join(",")} // 선택된 ID들 문자열로 연결
              />
              
              <Field label="리포트 유형" required />
              <Select
                name="report_type"
                options={reportTypeOptions}
                value={selectedType}
                onChange={(val) => {
                  setSelectedType(val as ReportJobType)
                  setValue("report_type", val as ReportJobType)
                }}
                isRequired
                portalled={false}
              />

              <Field label="생성 주기" required />
              <Select
                name="schedule"
                options={filteredScheduleOptions}
                value={selectedSchedule}
                onChange={(val) => {
                  setSelectedSchedule(val as ReportJobSchedule)
                  setValue("schedule", val as ReportJobSchedule)
                }}
                isRequired
                portalled={false}
              />


              {selectedSchedule !== "once" && (
                <>
                  <Field label="생성 시간" required>
                    <Input
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                    />
                  </Field>

                  <Field label="생성 기간 종료일" required>
                    <Input
                      type="date"
                      value={endDate}
                      min={new Date().toISOString().split("T")[0]}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Field>
                </>
              )}
            </VStack>
          </DialogBody>

          <DialogFooter gap={2}>
            <ButtonGroup>
              <DialogActionTrigger asChild>
                <Button variant="subtle" colorPalette="gray" disabled={isSubmitting}>
                  취소
                </Button>
              </DialogActionTrigger>
              <Button
                type="submit"
                variant="solid"
                disabled={!isValid || isSubmitting || hasSubmitted}
                loading={isSubmitting}
              >
                저장
              </Button>
            </ButtonGroup>
          </DialogFooter>
        </form>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  )
}

export default ReportSetting
